import { type FC, useMemo } from "react";
import { useAnnieAvatar } from "@/hooks/useAnnieAvatar";
import { ASSISTANT_AVATAR } from "@/theme/tokens";
import { AssistantAvatar } from "@/ui/AssistantAvatar/AssistantAvatar";
import { AssistantAvatarMood } from "@/ui/AssistantAvatar/assistantAvatarConfig";
import { WalkthroughController } from "@/ui/Walkthrough/WalkthroughController";
import { type WalkthroughGuide, WalkthroughGuideBeat } from "@/ui/Walkthrough/walkthroughGuidePose";
import type { AnnieWalkthrough } from "@/utils/annieStream";

type Props = {
	walkthrough: AnnieWalkthrough | null;
	onFinish: () => void;
	onRunningChange: (isRunning: boolean) => void;
};

const MOOD_BY_BEAT: Record<WalkthroughGuideBeat, AssistantAvatarMood> = {
	[WalkthroughGuideBeat.Arriving]: AssistantAvatarMood.Idle,
	[WalkthroughGuideBeat.Talking]: AssistantAvatarMood.Talking,
	[WalkthroughGuideBeat.Pointing]: AssistantAvatarMood.Idle,
	[WalkthroughGuideBeat.Oops]: AssistantAvatarMood.Oops,
	[WalkthroughGuideBeat.Done]: AssistantAvatarMood.Done,
};

/** Annie's walkthrough host: the generic controller with the user's Annie walking the tour. */
export const AnnieWalkthroughController: FC<Props> = ({ walkthrough, onFinish, onRunningChange }) => {
	const { visibleConfig } = useAnnieAvatar();
	const guide = useMemo<WalkthroughGuide | undefined>(
		() =>
			visibleConfig === null
				? undefined
				: {
						size: ASSISTANT_AVATAR.SIZE.TOUR_GUIDE,
						render: (pose) => (
							<AssistantAvatar
								config={visibleConfig}
								mood={MOOD_BY_BEAT[pose.beat]}
								size={pose.size}
								animated
								point={pose.point}
								gaze={pose.gaze}
								dataTestId="annie-walkthrough-avatar"
							/>
						),
					},
		[visibleConfig],
	);
	return <WalkthroughController walkthrough={walkthrough} onFinish={onFinish} onRunningChange={onRunningChange} guide={guide} />;
};
